import React, { useState, useEffect } from "react";
import {
  X,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Copy,
  Activity,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";
import { toast } from "react-hot-toast";
import webhooksService, {
  WebhookLog,
  WebhookLogsParams,
  Webhook,
} from "../../services/webhooks.service";
import Loader from "../../widgets/Loader";

interface WebhookLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
  webhook: Webhook;
}

const WebhookLogsModal: React.FC<WebhookLogsModalProps> = ({
  isOpen,
  onClose,
  webhook,
}) => {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [, setHasMore] = useState(false);
  const pageSize = 10;
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && webhook) {
      fetchLogs();
    } else if (!isOpen) {
      // Reset state when modal is closed
      setLogs([]);
      setCurrentPage(1);
      setExpandedLogId(null);
    }
  }, [isOpen, webhook, currentPage]);

  const fetchLogs = async () => {
    if (!webhook) return;

    try {
      setIsLoading(true);
      const params: WebhookLogsParams = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        webhook_config_id: webhook._id,
      };

      const response = await webhooksService.getWebhookLogs(params);

      // Handle the actual API response structure
      const logsData = response.data?.items || [];
      const totalCountData = response.data?.total_count || 0;

      setLogs(logsData);
      setTotalCount(totalCountData);
      setHasMore(logsData.length === pageSize);
    } catch (error) {
      console.error("Error fetching webhook logs:", error);
      toast.error("Failed to fetch webhook logs");
      // Set empty state on error
      setLogs([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "SUCCESS":
        return {
          color: "bg-green-100 text-green-800 border-green-200",
          icon: CheckCircle,
          iconColor: "text-green-600",
          bgColor: "bg-green-50",
        };
      case "FAILED":
        return {
          color: "bg-red-100 text-red-800 border-red-200",
          icon: XCircle,
          iconColor: "text-red-600",
          bgColor: "bg-red-50",
        };
      case "PENDING":
        return {
          color: "bg-yellow-100 text-yellow-800 border-yellow-200",
          icon: Clock,
          iconColor: "text-yellow-600",
          bgColor: "bg-yellow-50",
        };
      case "RETRY":
        return {
          color: "bg-blue-100 text-blue-800 border-blue-200",
          icon: Clock,
          iconColor: "text-blue-600",
          bgColor: "bg-blue-50",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800 border-gray-200",
          icon: AlertTriangle,
          iconColor: "text-gray-600",
          bgColor: "bg-gray-50",
        };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const toggleLogExpand = (logId: string) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-gray-600 bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Canvas */}
        <section className="absolute inset-y-0 right-0 pl-10 max-w-full flex">
          <div className="relative w-screen max-w-2xl">
            <div className="h-full flex flex-col bg-white shadow-xl">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center">
                      <Activity className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Webhook Logs
                      </h3>
                      <p className="text-sm text-gray-500">
                        {webhook.webhook_name}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Summary */}
                <div className="mt-4 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Total:</span> {totalCount}{" "}
                    logs
                  </div>
                  <div className="text-sm text-gray-500 font-mono">
                    {webhook.webhook_url}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader />
                  </div>
                ) : !logs || logs.length === 0 ? (
                  <div className="text-center py-12 px-6">
                    <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No logs found
                    </h3>
                    <p className="text-gray-500">
                      This webhook hasn't been triggered yet or logs are not
                      available.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {(logs || []).map((log) => {
                      const statusConfig = getStatusConfig(log.status);
                      const isExpanded = expandedLogId === log._id;

                      return (
                        <div
                          key={log._id}
                          className="px-6 py-4 hover:bg-gray-50 transition-colors"
                        >
                          {/* Log Header */}
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <statusConfig.icon
                                className={`w-5 h-5 ${statusConfig.iconColor} mt-0.5 flex-shrink-0`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}
                                  >
                                    {log.status}
                                  </span>
                                  <span className="text-sm font-medium text-gray-900">
                                    {log.webhook_config.webhook_method}
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    {log.trigger_type}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500">
                                  {formatDate(log.createdAt)}
                                </p>
                                {log.error_message && (
                                  <p className="text-xs text-red-600 mt-1 truncate">
                                    {log.error_message}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-1 ml-4">
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    JSON.stringify(log, null, 2),
                                    "Log"
                                  )
                                }
                                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                title="Copy log"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => toggleLogExpand(log._id)}
                                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                title={
                                  isExpanded ? "Collapse" : "Expand details"
                                }
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Expanded Details */}
                          {isExpanded && (
                            <div className="mt-4 space-y-4 border-t border-gray-100 pt-4">
                              {/* Webhook Config */}
                              <div
                                className={`p-3 rounded-lg ${statusConfig.bgColor}`}
                              >
                                <h4 className="text-sm font-medium text-gray-900 mb-2">
                                  Webhook Configuration
                                </h4>
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">
                                      Method:
                                    </span>
                                    <span className="font-mono">
                                      {log.webhook_config.webhook_method}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">URL:</span>
                                    <span className="font-mono text-xs truncate ml-2">
                                      {log.webhook_config.webhook_url}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Payload */}
                              {log.payload && (
                                <div className="bg-gray-50 p-3 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-medium text-gray-900">
                                      Payload
                                    </h4>
                                    <button
                                      onClick={() =>
                                        copyToClipboard(
                                          JSON.stringify(log.payload, null, 2),
                                          "Payload"
                                        )
                                      }
                                      className="text-xs text-blue-600 hover:text-blue-800"
                                    >
                                      Copy
                                    </button>
                                  </div>
                                  <pre className="text-xs text-gray-700 bg-white p-2 rounded border overflow-x-auto max-h-40">
                                    {JSON.stringify(log.payload, null, 2)}
                                  </pre>
                                </div>
                              )}

                              {/* Response */}
                              {log.response && (
                                <div className="bg-gray-50 p-3 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-medium text-gray-900">
                                      Response
                                    </h4>
                                    <button
                                      onClick={() =>
                                        copyToClipboard(
                                          JSON.stringify(log.response, null, 2),
                                          "Response"
                                        )
                                      }
                                      className="text-xs text-blue-600 hover:text-blue-800"
                                    >
                                      Copy
                                    </button>
                                  </div>
                                  <pre className="text-xs text-gray-700 bg-white p-2 rounded border overflow-x-auto max-h-40">
                                    {JSON.stringify(log.response, null, 2)}
                                  </pre>
                                </div>
                              )}

                              {/* Error Details */}
                              {log.error_message && (
                                <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                                  <h4 className="text-sm font-medium text-red-900 mb-2">
                                    Error Details
                                  </h4>
                                  <p className="text-sm text-red-700">
                                    {log.error_message}
                                  </p>
                                </div>
                              )}

                              {/* Metadata */}
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <h4 className="text-sm font-medium text-gray-900 mb-2">
                                  Metadata
                                </h4>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="text-gray-500">
                                      Created:
                                    </span>
                                    <div className="font-mono">
                                      {formatDate(log.createdAt)}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">
                                      Updated:
                                    </span>
                                    <div className="font-mono">
                                      {formatDate(log.updatedAt)}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">
                                      Webhook ID:
                                    </span>
                                    <div className="font-mono text-xs truncate">
                                      {log.webhook_id}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">
                                      Status:
                                    </span>
                                    <div className="font-semibold">
                                      {log.status}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="border-t border-gray-200 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Showing{" "}
                        <span className="font-medium">
                          {(currentPage - 1) * pageSize + 1}
                        </span>{" "}
                        to{" "}
                        <span className="font-medium">
                          {Math.min(currentPage * pageSize, totalCount)}
                        </span>{" "}
                        of <span className="font-medium">{totalCount}</span>{" "}
                        logs
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>

                        {Array.from(
                          { length: Math.min(3, totalPages) },
                          (_, i) => {
                            let pageNum;
                            if (totalPages <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage <= 2) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 1) {
                              pageNum = totalPages - 2 + i;
                            } else {
                              pageNum = currentPage - 1 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`relative inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md ${
                                  currentPage === pageNum
                                    ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                    : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          }
                        )}

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRightIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default WebhookLogsModal;
